import React, { useEffect, useState } from "react";
import { EditOutlined } from "@ant-design/icons";
import { message } from "antd";
import "./style.css";
import GenericModalForm from "../../../../Components/Modals/GenericModalForm";
import {
  getFuelAuthorizationConfig,
  updateFuelAuthorizationConfig,
} from "../../../../API/Admin/SystemConfiguration/FuelAuthorizationConfig";

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const CONFIG_FIELDS = [
  {
    name: "sensor_tolerance_pct",
    label: "Sensor Tolerance",
    unit: "%",
    min: 0,
    max: 100,
    step: 1,
  },
  {
    name: "mileage_tolerance_pct",
    label: "Mileage Tolerance",
    unit: "%",
    min: 0,
    max: 100,
    step: 1,
  },
  {
    name: "odometer_gps_mismatch_pct",
    label: "Odometer/GPS Mismatch Tolerance",
    unit: "%",
    min: 0,
    max: 100,
    step: 1,
  },
  {
    name: "geofence_radius_m",
    label: "Geofence Radius",
    unit: "m",
    min: 0,
    step: 1,
  },
  {
    name: "cooldown_minutes",
    label: "Cooldown Period",
    unit: "min",
    min: 0,
    step: 1,
  },
  {
    name: "tracker_offline_minutes",
    label: "Tracker Offline Threshold",
    unit: "min",
    min: 0,
    step: 1,
  },
  {
    name: "stuck_fuel_epsilon_l",
    label: "Stuck Fuel Epsilon",
    unit: "L",
    min: 0,
    step: 0.01,
  },
  {
    name: "stuck_distance_threshold_km",
    label: "Stuck Distance Threshold",
    unit: "km",
    min: 0,
    step: 1,
  },
  {
    name: "authorization_ttl_hours",
    label: "Authorization TTL",
    unit: "hrs",
    min: 0,
    step: 1,
  },
  {
    name: "signature_request_ttl_minutes",
    label: "Signature Request TTL",
    unit: "min",
    min: 0,
    step: 1,
  },
];

const DEFAULT_CONFIG = CONFIG_FIELDS.reduce(
  (acc, field) => ({ ...acc, [field.name]: 0 }),
  {},
);

const FuelAuthorization = ({ setBtnConfigs }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await getFuelAuthorizationConfig();
      const data = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && data) {
        setConfig(data);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to load fuel authorization config."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to load fuel authorization config.",
        ),
      );
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Edit Configuration",
        icon: <EditOutlined />,
        onClick: () => setIsEditOpen(true),
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBtnConfigs]);

  const handleSave = async (values) => {
    try {
      const payload = CONFIG_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.name]: values[field.name] }),
        {},
      );

      const res = await updateFuelAuthorizationConfig(payload);

      if (Math.floor(res?.status / 100) === 2) {
        setConfig(res?.data?.data || payload);
        message.success(
          res?.data?.message ||
            "Fuel authorization config updated successfully",
        );
        setIsEditOpen(false);
      } else {
        message.error(
          getErrorMessage(
            res?.data,
            "Failed to update fuel authorization config.",
          ),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to update fuel authorization config.",
        ),
      );
    }
  };

  return (
    <div className="fuel-authorization">
      <div className="config-card">
        <div className="config-grid">
          {CONFIG_FIELDS.map((field) => (
            <div className="config-field" key={field.name}>
              <label className="config-label">{field.label}</label>
              <div className="config-value-box">
                {config[field.name]} {field.unit}
              </div>
            </div>
          ))}
        </div>
      </div>

      <GenericModalForm
        open={isEditOpen}
        title="Edit Fuel Authorization Configuration"
        subtitle="Update the fraud-detection and authorization thresholds"
        okText="Save Changes"
        cancelText="Cancel"
        fields={CONFIG_FIELDS.map((field) => ({
          name: field.name,
          label: `${field.label} (${field.unit})`,
          placeholder: "0",
          type: "inputNumber",
          min: field.min,
          max: field.max,
          step: field.step,
          rules: [{ required: true, message: `${field.label} is required` }],
        }))}
        initialValues={CONFIG_FIELDS.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: parseFloat(config[field.name]) || 0,
          }),
          {},
        )}
        onCancel={() => setIsEditOpen(false)}
        onOk={handleSave}
      />
    </div>
  );
};

export default FuelAuthorization;
